/**
 * What a criterion measures, drawn beside its label wherever a person reads a
 * value on it, so "Impact: 8" reads with its beneficiary, baseline and scale
 * (owner ruling 2026-10-28; migration 967). Draws nothing when the criterion
 * says none of the three: the warning belongs where criteria are set, not on
 * every value.
 */
import { Info } from 'lucide-react';
import { Tooltip } from '@/components/ds';
import { useCriterionReading } from '@/hooks/prioritization/useCriterionReading';
import { criterionReadingLines, criterionReadingSummary } from '@/lib/criterionReading';

export interface CriterionReadingHintProps {
  /** The attribute the criterion is over (a criterion is an attribute selected for evaluation). */
  attributeKey: string;
  /**
   * `line`: one line of small text under the label.
   * `mark`: a small mark beside the label that shows the reading on hover or
   * focus, for narrow table cells.
   */
  variant?: 'line' | 'mark';
  className?: string;
}

export function CriterionReadingHint({ attributeKey, variant = 'line', className = '' }: CriterionReadingHintProps) {
  const reading = useCriterionReading(attributeKey);
  const summary = criterionReadingSummary(reading);
  if (!summary) return null;

  if (variant === 'mark') {
    return (
      <Tooltip
        className={className}
        content={
          <span className="flex flex-col gap-0.5">
            {criterionReadingLines(reading).map((l) => (
              <span key={l.label}>
                <span className="text-[var(--text-ter)]">{l.label}:</span> {l.text}
              </span>
            ))}
          </span>
        }
      >
        <span
          tabIndex={0}
          role="img"
          aria-label={summary}
          data-testid="criterion-reading-mark"
          className="inline-flex text-[var(--text-ter)] hover:text-[var(--text-sec)] focus:text-[var(--text-sec)] focus:outline-none"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </span>
      </Tooltip>
    );
  }

  return (
    <p data-testid="criterion-reading" className={`text-xs normal-case tracking-normal leading-snug text-[var(--text-ter)] ${className}`}>
      {summary}
    </p>
  );
}

export default CriterionReadingHint;
